import React, {
    Component,
    MouseEvent
} from 'react';
import {
    bindActionCreators,
    Dispatch
} from 'redux';
import {connect} from 'react-redux';

import {
    ConfirmPopup,
    confirmPopup
} from 'primereact/confirmpopup';
import {Toolbar as PrimeToolbar} from 'primereact/toolbar';

import {__} from '~/shared/src/utilities/messageCatalog';
import metaModel from '~/shared/src/utilities/metaModel';
import {getXmlForContext} from '~/shared/src/utilities/xmlExport';
import Item from '~/shared/src/ci/Item';
import Reference from '~/shared/src/ci/Reference';
import ciHelper from '~/shared/src/utilities/dataUtils';
import User from '~/shared/src/model/User';
import PropertySet from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';
import {
    LoadReferencesForExportParameters,
    LoadReferencesForExportResult
} from '~/shared/src/utilities/modules/finder';

import {StoreState} from '~/frontend/src/store';
import {getModifiedData} from '~/frontend/src/utilities/dataUtils';
import {fileDownload} from '~/frontend/src/utilities/fileDownload';
import {
    getToolbarComponents,
    ToolbarComponents,
    ToolbarLayoutComponents
} from '~/frontend/src/utilities/toolbars';
import xhr from '~/frontend/src/utilities/xhr';
import {ComponentProps as DetailProps} from '~/frontend/src/modules/panels/Finder/detail';

import * as PanelActions from '~/frontend/src/actions/panel/common';
import {SetParamAction} from '~/frontend/src/actions/panel/common/setParam';
import * as FinderActions from '~/frontend/src/actions/panel/finderDetail';
import {LoadData} from '~/frontend/src/actions/panel/finderDetail/loadData';
import {SaveData} from '~/frontend/src/actions/panel/finderDetail/saveData';
import {SetAdvancedSearchOptions} from '~/frontend/src/actions/panel/finderDetail/setAdvancedSearchOptions';
import {setMessage} from '~/frontend/src/actions/intercom';
import {BaseRequestParameters} from '~/shared/src/utilities/modules/common';


const Actions = Object.assign( { setMessage }, PanelActions, FinderActions ) as any;
type SetParam = <K extends keyof DetailProps>( panelKey : string, paramKey : K, paramValue : DetailProps[K] ) => SetParamAction;


type ReferenceCounts = {
    dest : { [ referenceSetId : number ] : number };
    src : { [ referenceSetId : number ] : number }
};


const defaultProps = {
    /**
     * Should the save Button shown in the toolbar <strong>or</strong> not.
     */
    showSaveButton: true as boolean,

    /**
     * The error count to show as badge on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true.
     */
    saveButtonErrorCount: 0 as number,

    /**
     * The error tooltip to show on save button.
     *
     * Only applicable if <prop>showSaveButton</prop> is set to true AND <prop>saveButtonErrorCount</prop> is set to an positive integer.
     */
    saveButtonErrorTooltip: '' as string | JSX.Element | ( string | JSX.Element )[],

    /**
     * Should the clone Button shown in the toolbar <strong>or</strong> not.
     */
    showCloneButton: true as boolean,

    /**
     * Should the restore Button shown in the toolbar <strong>or</strong> not.
     */
    showRestoreButton: false as boolean,

    /**
     * Should the delete Button shown in the toolbar <strong>or</strong> not.
     */
    showDeleteButton: true as boolean,

    /**
     * Should the history Button shown in the toolbar <strong>or</strong> not.
     */
    showHistoryButton: true as boolean,

    /**
     * Show copy XML button.
     */
    showCopyXmlButton: true as boolean,

    /**
     * Show download XML button.
     */
    showDownloadXmlButton: true as boolean,

    /**
     * Should the AdvancedSearchOptionsMenu shown in the toolbar <strong>or</strong> not.
     */
    showAdvancedSearchOptions: true as boolean,

    showPanelSelectorDropdown: true as boolean,

    /**
     * Should the view options button shown in the toolbar.
     */
    showViewOptionsButton: true as boolean,

    /**
     * Show reload button.
     */
    showReloadButton: true as boolean
};

type DefaultProps = typeof defaultProps;

type Props = {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The database schema to filter data and metadata for.
     */
    schema : Schema;

    /**
     * The status to filter data for.
     */
    status : Status;

    /**
     * The timestamp to filter data at.
     */
    timestamp : string;

    /**
     * The timezone to filter data at.
     */
    timezone : string;

    /**
     * The Item to filter data and metadata for.
     */
    item? : Item;

    /**
     * The reference counts by direction and referenceSetId
     */
    referenceCounts? : ReferenceCounts;

    /**
     * Should the MetaModel information shown as defined (raw) <strong>or</strong> should they be processed by MessageCatalog.
     */
    rawMode : boolean;

    /**
     * Should the MetaModel information as defined additionally shown for processed information <strong>or</strong> not.
     * This applies in the form: &lt;processed MetaModel information&gt; (&lt;defined MetaModel information&gt;)
     *
     * Only applicable if <prop>rawMode</prop> is set to false.
     */
    showAdditionalRawNames : boolean;

    showHistoryPopup: boolean;
};

type StateProps = {

    /**
     * The current user.
     *
     * Will be mapped from global state.
     */
    user? : User;
};

/**
 * Function props mapped by mapDispatchToProps
 */
type ActionProps = {
    // common panel actions
    setParam : SetParam;

    // finder actions
    loadData : LoadData;
    saveData : SaveData;
    setAdvancedSearchOptions : SetAdvancedSearchOptions;

    // intercom actions
    setMessage : typeof setMessage;
}

export type ComponentProps = Partial<DefaultProps> & Props;
type AllProps = DefaultProps & Props & StateProps & ActionProps;

type State = {
    item : Item | undefined;
    isItemUpdatable : boolean;
    isItemDeletable : boolean;
    isItemInsertable : boolean;
    isItemTypeInsertable : boolean;
};


/**
 * Toolbar
 */
class Toolbar extends Component<AllProps, State> {

    static defaultProps = defaultProps;

    /**
     * @param props
     */
    constructor( props : AllProps ) {
        super( props );

        this.state = {
            item                : undefined,
            isItemUpdatable     : false,
            isItemDeletable     : false,
            isItemInsertable    : false,
            isItemTypeInsertable: false
        };

        this.onSaveButtonClick = this.onSaveButtonClick.bind( this );
        this.onCloneButtonClick = this.onCloneButtonClick.bind( this );
        this.onRestoreButtonClick = this.onRestoreButtonClick.bind( this );
        this.onDeleteButtonClick = this.onDeleteButtonClick.bind( this );
        this.onHistoryButtonClick = this.onHistoryButtonClick.bind( this );
        this.onCopyXmlApply = this.onCopyXmlApply.bind( this );
        this.onDownloadXmlApply = this.onDownloadXmlApply.bind( this );
        this.onAdvancedSearchOptionsMenuChange = this.onAdvancedSearchOptionsMenuChange.bind( this );
        this.onViewOptionChange = this.onViewOptionChange.bind( this );
        this.onReloadButtonClick = this.onReloadButtonClick.bind( this );
    }

    /**
     * REDUX stuff
     */
    static getDerivedStateFromProps( props : AllProps, state : State ) : Partial<State> {
        const {
                  schema,
                  timestamp,
                  item,
                  user
              }                       = props,
              retVal : Partial<State> = {
                  item                : undefined,
                  isItemUpdatable     : false,
                  isItemDeletable     : false,
                  isItemInsertable    : false,
                  isItemTypeInsertable: false
              };

        // editable
        if ( item ) {
            const itemTypeId            = item.itemTypeId,
                  itemEditable          = itemTypeId > 0 && timestamp === 'now' && user?.hasGenericPermissionOnItemType( 'nonBlocked', itemTypeId, schema ),
                  editableStatusIds     = metaModel[ schema ].getEditableStatus().map( status => status.id ),
                  itemHasEditableStatus = editableStatusIds.includes( item.statusId );

            if ( itemEditable ) {

                // updatable
                retVal.isItemUpdatable = itemHasEditableStatus && ( user?.hasPermissionOnItemType( 'update', itemTypeId, schema ) ?? false );

                // deletable
                retVal.isItemDeletable = itemHasEditableStatus && ( user?.hasPermissionOnItemType( 'delete', itemTypeId, schema ) ?? false );

                // insertable
                retVal.isItemInsertable = user?.hasPermissionOnItemType( 'insert', itemTypeId, schema ) ?? false;
            }

            retVal.isItemTypeInsertable = user?.hasPermissionOnItemType( 'insert', itemTypeId, schema ) ?? false;
        }

        if ( Object.keys( retVal ).length ) {
            return retVal;
        }
        else {
            return {};
        }
    }

    /**
     * Handler for Save button click.
     *
     * @private
     */
    onSaveButtonClick() {
        this.props.saveData( this.props.uid, this.props.schema );
    }

    /**
     * Handler for clone Button click.
     *
     * @private
     */
    onCloneButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        confirmPopup( {
            target         : event.currentTarget,
            message        : 'Not implemnted yet!',
            className      : 'yn-confirm-popup-notimplemented',
            icon           : 'pi pi-exclamation-triangle yn-warning',
            acceptLabel    : __( 'YN.base.common.yes' ),
            acceptClassName: 'p-button p-button-outlined',
            rejectLabel    : __( 'YN.base.common.no' ),
            rejectClassName: 'p-button-secondary p-button-outlined mr-3'
        } );
    }

    /**
     * Handler for restore Button click.
     *
     * @private
     */
    onRestoreButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        confirmPopup( {
            target         : event.currentTarget,
            message        : 'Not implemnted yet!',
            className      : 'yn-confirm-popup-notimplemented',
            icon           : 'pi pi-exclamation-triangle yn-warning',
            acceptLabel    : __( 'YN.base.common.yes' ),
            acceptClassName: 'p-button-success p-button-outlined',
            rejectLabel    : __( 'YN.base.common.no' ),
            rejectClassName: 'p-button-secondary p-button-outlined mr-3'
        } );
    }

    /**
     * Handler for delete Button click.
     *
     * @private
     */
    onDeleteButtonClick() {
        if ( this.props.item ) {
            this.props.item.reset();
            this.props.item.statusId = metaModel[ this.props.schema ].getStatus( 'deleted' )!.id;
            this.props.saveData(
                this.props.uid,
                this.props.schema,
                [
                    this.props.uid,
                    'toast',
                    'deleteSuccess',
                    {
                        severity: 'success',
                        summary : __( 'YN.base.finder.save.deleteSuccess.title' ),
                        detail  : __( 'YN.base.finder.save.deleteSuccess.text' )
                    }
                ] );
        }
    }

    /**
     * Handler for history Button click.
     *
     * @private
     */
    onHistoryButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        this.props.setParam( this.props.uid, 'showHistoryPopup', !this.props.showHistoryPopup );
    }

    async onCopyXmlApply( selectedPropertySets : PropertySet[], selectedEnumPropertySets : PropertySet[], selectedReferenceSets : {
        dest : ReferenceSet[];
        src : ReferenceSet[];
    } ) {
        if ( this.props.item ) {
            const dataContext = await this.createExportContext( selectedPropertySets, selectedEnumPropertySets, selectedReferenceSets );

            await navigator.clipboard.writeText( getXmlForContext( dataContext, [ this.props.item.id ] ) );

            delete global.data[ dataContext ];

            this.props.setMessage?.(
                this.props.uid,
                'toast',
                'xmlDataCopiedToClipboard',
                {
                    severity: 'info',
                    summary : __( 'YN.base.common.xml.dataCopiedToClipboard.title' ),
                    detail  : __( 'YN.base.common.xml.dataCopiedToClipboard.text' )
                }
            );
        }
    }

    async onDownloadXmlApply( selectedPropertySets : PropertySet[], selectedEnumPropertySets : PropertySet[], selectedReferenceSets : {
        dest : ReferenceSet[];
        src : ReferenceSet[];
    } ) {
        if ( this.props.item ) {
            const dataContext = await this.createExportContext( selectedPropertySets, selectedEnumPropertySets, selectedReferenceSets ),
                  fileName    = `${this.props.item.itemType.name}__${this.props.item.key}`;

            fileDownload( getXmlForContext( dataContext, [ this.props.item.id ] ), fileName, 'xml' );

            delete global.data[ dataContext ];
        }
    }

    /**
     * Handler for the AdvancedSearchOptionsMenu change event.
     */
    onAdvancedSearchOptionsMenuChange( status : Status, timezone : string, timestamp : string ) {
        this.props.setAdvancedSearchOptions( this.props.uid, status, timezone, timestamp );
    }

    /**
     * The handler for view option change.
     *
     * @param {String} name
     * @param {Boolean} checked
     *
     * @private
     */
    onViewOptionChange( name : string, checked : boolean ) {
        this.props.setParam( this.props.uid, name as keyof DetailProps, checked );
    }

    /**
     * Handler for reload Button click.
     *
     * @private
     */
    onReloadButtonClick( event : MouseEvent<HTMLButtonElement> ) {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  item
              } = this.props;

        if ( item ) {
            this.props.loadData( uid, schema, status, timestamp, timezone, item.id );
        }
    }

    async createExportContext( selectedPropertySets : PropertySet[], selectedEnumPropertySets : PropertySet[], selectedReferenceSets : {
        dest : ReferenceSet[];
        src : ReferenceSet[];
    } ) : Promise<string> {
        const retVal = String.getHash(),
              {
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  item
              }      = this.props;

        if ( item ) {
            const itemData = item.toJSON();

            // remove properties
            if ( itemData.properties ) {
                for ( let i = itemData.properties.length - 1; i >= 0; i-- ) {
                    if ( !selectedPropertySets.find( pSet => pSet.id === itemData.properties![ i ].propertySetId ) ) {
                        itemData.properties.splice( i, 1 );
                    }
                }
            }

            // remove enumerated properties
            if ( itemData.enumProperties ) {
                for ( let i = itemData.enumProperties.length - 1; i >= 0; i-- ) {
                    if ( !selectedEnumPropertySets.find( pSet => pSet.id === itemData.enumProperties![ i ].propertySetId ) ) {
                        itemData.enumProperties.splice( i, 1 );
                    }
                }
            }

            new Item( itemData, retVal, item.schema );

            // get references from backend
            if ( selectedReferenceSets.dest.length || selectedReferenceSets.src.length ) {
                const params : BaseRequestParameters & LoadReferencesForExportParameters = {
                    schema,
                    status,
                    timestamp,
                    timezone,
                    itemId         : item.id,
                    referenceSetIds: {
                        dest: selectedReferenceSets.dest.map( rSet => rSet.id ),
                        src : selectedReferenceSets.src.map( rSet => rSet.id )
                    }
                };

                const response = await xhr.post(
                    `${BASE_URL}/api/finder/loadReferencesForExport`,
                    params,
                    undefined,
                    uid,
                    'loadReferencesForExport'
                );

                if ( response?.data ) {
                    const responseData = response.data as LoadReferencesForExportResult;

                    for ( const referenceItem of responseData.data.items ) {
                        const existingItem = ciHelper.findItem( retVal, referenceItem.itemTypeId, referenceItem.key! );

                        if ( !existingItem ) {
                            new Item( referenceItem, retVal, item.schema );
                        }
                    }

                    for ( const reference of responseData.data.references ) {
                        const existingReference = ciHelper.findReference( retVal, reference.referenceSetId, reference.srcItemId, reference.destItemId );

                        if ( !existingReference ) {
                            new Reference( reference, retVal, item.schema );
                        }
                    }
                }
            }
        }

        return retVal;
    }

    getExportParameters() {
        const retVal                                 = {
                  allPropertySets           : [] as PropertySet[],
                  selectablePropertySets    : [] as PropertySet[],
                  selectedPropertySets      : [] as PropertySet[],
                  allEnumPropertySets       : [] as PropertySet[],
                  selectableEnumPropertySets: [] as PropertySet[],
                  selectedEnumPropertySets  : [] as PropertySet[],
                  allReferenceSets          : {
                      dest: [],
                      src : []
                  } as { dest : ReferenceSet[]; src : ReferenceSet[]; },
                  selectableReferenceSets   : {
                      dest: [],
                      src : []
                  } as { dest : ReferenceSet[]; src : ReferenceSet[]; },
                  selectedReferenceSets     : {
                      dest: [],
                      src : []
                  } as { dest : ReferenceSet[]; src : ReferenceSet[]; },
                  referenceItemType         : ''
              },
              {
                  item,
                  referenceCounts
              }                                      = this.props,
              existingDestReferenceSetIds : number[] = Object.keys( referenceCounts?.dest ?? {} ).map( refSetId => Number( refSetId ) ),
              existingSrcReferenceSetIds : number[]  = Object.keys( referenceCounts?.src ?? {} ).map( refSetId => Number( refSetId ) );

        if ( item ) {
            for ( const propertySet of item.itemType.propertySets ) {
                if ( propertySet.isEnumerated ) {
                    retVal.allEnumPropertySets.push( propertySet );
                }
                else {
                    retVal.allPropertySets.push( propertySet );
                }
            }

            retVal.selectedPropertySets = retVal.allPropertySets;
            item.properties.forEach( ( property, propertyTypeId ) => {
                retVal.selectablePropertySets.push( property.propertySet );
            } );

            retVal.selectedEnumPropertySets = retVal.allEnumPropertySets;
            item.enumProperties.forEach( ( property, propertyTypeId ) => {
                retVal.selectableEnumPropertySets.push( property.propertySet );
            } );

            retVal.allReferenceSets.dest = metaModel[ item.schema ].getReferenceSetsForItemType( item.itemTypeId, 'dest' ) ?? [];
            retVal.selectedReferenceSets.dest = retVal.allReferenceSets.dest;
            for ( const referenceSetId of existingDestReferenceSetIds ) {
                const referenceSet = retVal.allReferenceSets.dest.find( refSet => refSet.id === referenceSetId );

                if ( referenceSet ) {
                    retVal.selectableReferenceSets.dest.push( referenceSet );
                }
            }

            retVal.allReferenceSets.src = metaModel[ item.schema ].getReferenceSetsForItemType( item.itemTypeId, 'src' ) ?? [];
            retVal.selectedReferenceSets.src = retVal.allReferenceSets.src;
            for ( const referenceSetId of existingSrcReferenceSetIds ) {
                const referenceSet = retVal.allReferenceSets.src.find( refSet => refSet.id === referenceSetId );

                if ( referenceSet ) {
                    retVal.selectableReferenceSets.src.push( referenceSet );
                }
            }

            retVal.referenceItemType = item.itemType.name;
        }

        return retVal;
    }

    /**
     * REACT lifecycle method
     */
    render() {
        const {
                  uid,
                  schema,
                  status,
                  timestamp,
                  timezone,
                  rawMode,
                  item,
                  showAdditionalRawNames,
                  showSaveButton,
                  saveButtonErrorCount,
                  saveButtonErrorTooltip,
                  showCloneButton,
                  showRestoreButton,
                  showDeleteButton,
                  showHistoryButton,
                  showCopyXmlButton,
                  showDownloadXmlButton,
                  showAdvancedSearchOptions,
                  showViewOptionsButton,
                  showPanelSelectorDropdown,
                  showReloadButton
              }                                          = this.props,
              {
                  isItemUpdatable,
                  isItemDeletable,
                  isItemInsertable,
                  isItemTypeInsertable
              }                                          = this.state,
              layoutComponents : ToolbarLayoutComponents = {
                  left : [],
                  right: []
              };

        // left
        if ( showSaveButton ) {
            layoutComponents.left.push( {
                type  : 'saveButton',
                saveButtonErrorCount,
                saveButtonErrorTooltip,
                params: {
                    key: `${uid}_saveButton`,

                    disabled: !isItemUpdatable,
                    onClick : this.onSaveButtonClick
                }
            } );
        }

        if ( showCloneButton ) {
            layoutComponents.left.push( {
                type  : 'cloneButton',
                params: {
                    key: `${uid}_cloneButton`,

                    disabled: !isItemUpdatable || !isItemTypeInsertable,
                    onClick : this.onCloneButtonClick
                }
            } );
        }

        if ( showDeleteButton ) {
            layoutComponents.left.push( {
                type  : 'deleteButton',
                params: {
                    key: `${uid}_deleteButton`,

                    disabled: !isItemDeletable,
                    confirm : true,
                    onClick : this.onDeleteButtonClick
                }
            } );
        }
        else if ( showRestoreButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_restoreButton`,

                    disabled: !isItemInsertable,

                    icon     : 'pi pi-plus-circle',
                    tooltip  : __( 'YN.base.restore.button' ),
                    onClick  : this.onRestoreButtonClick,
                    className: 'p-button-text p-button-success'
                }
            } );
        }

        layoutComponents.left.push( {
            type: 'separator'
        } );

        if ( showHistoryButton ) {
            layoutComponents.left.push( {
                type  : 'button',
                params: {
                    key: `${uid}_historyButton`,

                    icon     : 'pi pi-clock',
                    tooltip  : __( 'YN.base.history.button' ),
                    onClick  : this.onHistoryButtonClick,
                    className: 'p-button-text'
                }
            } );
        }

        layoutComponents.left.push( {
            type: 'separator'
        } );

        if ( showCopyXmlButton ) {
            layoutComponents.left.push( {
                type  : 'copyXml',
                params: {
                    key: `${uid}_copyXmlButton`,

                    uid,

                    ...this.getExportParameters(),

                    onApply: ( ...args ) => {
                        void this.onCopyXmlApply( ...args );
                    }
                }
            } );
        }

        if ( showDownloadXmlButton ) {
            layoutComponents.left.push( {
                type  : 'downloadXml',
                params: {
                    key: `${uid}_downloadXmlButton`,

                    uid,

                    ...this.getExportParameters(),

                    onApply: ( ...args ) => {
                        void this.onDownloadXmlApply( ...args );
                    }
                }
            } );
        }

        layoutComponents.left.push( {
            type: 'separator'
        } );

        if ( showAdvancedSearchOptions ) {
            layoutComponents.left.push( {
                type  : 'advancedSearchOptionsMenu',
                params: {
                    key: `${uid}_advancedSearchOptionsMenu`,
                    id : `${uid}_advancedSearchOptionsMenu`,

                    uid,

                    status,
                    timestamp,
                    timezone,
                    rawMode,
                    showAdditionalRawNames,
                    minDate: item?.creationTime
                        ? item.creationTime
                        : undefined,

                    markDirty: true,
                    onChange : this.onAdvancedSearchOptionsMenuChange
                }
            } );
        }

        // right (right to left!)
        if ( showPanelSelectorDropdown ) {
            const itemParams = item
                ? {
                    schema  : item.schema,
                    itemId  : item.id,
                    itemType: item.itemTypeId
                }
                : {};

            layoutComponents.right.push( {
                type  : 'panelSelectorDropdown',
                params: {
                    key: `${uid}_panelSelector`,

                    uid        : `${uid}_panelSelector`,
                    labeled    : true,
                    style      : { width: '18rem' },
                    currentView: `generic`,
                    ...itemParams
                }
            } );
        }

        if ( showViewOptionsButton ) {
            layoutComponents.right.push( {
                type  : 'viewOptionsMenu',
                params: {
                    key: `${uid}_viewOptions`,

                    uid: uid,

                    options           : [
                        {
                            name : 'rawMode',
                            label: __( 'YN.base.viewOptions.rawMode.label' ),
                            value: rawMode
                        },
                        {
                            name    : 'showAdditionalRawNames',
                            label   : __( 'YN.base.viewOptions.additionalRawNames.label' ),
                            value   : showAdditionalRawNames,
                            disabled: rawMode
                        }
                    ],
                    onViewOptionChange: this.onViewOptionChange
                }
            } );
        }

        layoutComponents.right.push( {
            type: 'separator'
        } );

        if ( showReloadButton ) {
            layoutComponents.right.push( {
                type  : 'reloadButton',
                params: {
                    key: `${uid}_reloadButton`,

                    confirm: () => {
                        const modifiedData = getModifiedData( uid, schema );

                        return Boolean( modifiedData.items.length || modifiedData.references.length );
                    },
                    onClick: this.onReloadButtonClick
                }
            } );
        }

        const toolbarComponents : ToolbarComponents = getToolbarComponents( layoutComponents );

        return <>
            <ConfirmPopup/>
            <PrimeToolbar
                left={toolbarComponents.left}
                right={toolbarComponents.right}
                className="yn-panel-toolbar shadow-4"
                style={{ zIndex: 5 }}
            />
        </>;
    }
}

/**
 * REDUX stuff
 */
function mapStateToProps( state : StoreState ) : StateProps {
    const retVal : StateProps = {
        user: undefined
    };

    // get some session user information
    if ( state.session?.user ) {
        retVal.user = state.session.user;
    }

    return retVal;
}

/**
 * REDUX stuff
 */
function mapDispatchToProps( dispatch : Dispatch ) {
    return bindActionCreators( Actions, dispatch );
}

export default connect<StateProps, ActionProps, ComponentProps, StoreState>( mapStateToProps, mapDispatchToProps )( Toolbar );
